import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Lock, Briefcase, Loader2 } from 'lucide-react';

const AdminLogin: React.FC = () => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(password);

    setLoading(false);

    if (result.success) {
      navigate('/admin/dashboard');
    } else {
      setError(result.message || 'Invalid password. Please try again.');
      setPassword('');
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center" 
      style={{ 
        background: 'var(--pw-gradient-hero)',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
      }}
    >
      <div className="max-w-md w-full mx-4">
        <div 
          className="rounded-2xl shadow-2xl p-10 backdrop-blur-sm"
          style={{ 
            background: 'var(--pw-gradient-card)',
            border: '1px solid var(--pw-surface-tertiary)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <div className="flex justify-center mb-8">
            <div 
              className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-light) 100%)' }}
            >
              <Briefcase className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <h2 
            className="text-3xl font-bold text-center mb-3" 
            style={{ 
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              letterSpacing: '-0.02em'
            }}
          >
            Admin Login
          </h2>
          <p 
            className="text-center mb-8 text-sm" 
            style={{ 
              color: 'var(--pw-muted-light)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
            Enter your password to access the admin panel
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label 
                htmlFor="password" 
                className="block text-sm font-medium mb-2" 
                style={{ 
                  color: 'var(--pw-text-main)',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                }}
              >
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5" style={{ color: 'var(--pw-muted-light)' }} />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
                  style={{ 
                    backgroundColor: 'var(--pw-surface-secondary)',
                    border: '1px solid var(--pw-surface-tertiary)',
                    color: 'var(--pw-text-main)'
                  }}
                  placeholder="Enter admin password"
                  required
                  autoFocus
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <div 
                className="border px-4 py-3 rounded-lg text-sm"
                style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: '#ef4444', color: '#ef4444' }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-medium transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              style={{ 
                background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-light) 100%)',
                color: 'var(--pw-text-main)',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.opacity = '1';
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a 
              href="/" 
              className="text-sm transition-colors"
              style={{ 
                color: 'var(--pw-muted-light)',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--pw-primary-light)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--pw-muted-light)';
              }}
            >
              ← Back to website
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;

